import { Suspense, useRef, useCallback } from 'react';
import { Canvas } from '@react-three/fiber';
import { CameraControls } from '@react-three/drei';
import { NoToneMapping } from 'three';
import { AdminSubject } from './AdminSubject';
import { SubjectDraft } from '@/contexts';
import { AreaConfig } from '#server/models';
import { Loader } from '@/components';
import { NavigationControlsEventListener } from '@/components';

interface AdminPreviewProps {
  draft: SubjectDraft;
  areaConfig?: AreaConfig;
  showLabels?: boolean;
  showAxes?: boolean;
  showOriginAxes?: boolean;
  activeLabelId?: string | null;
  onMeshClick?: (position: [number, number, number]) => void;
  onLabelClick?: (labelId: string) => void;
  onCameraControlsReady?: (controls: CameraControls | null) => void;
  placingLabelId?: string | null;
  forceVisible?: boolean;
  transformMode?: 'translate' | 'rotate' | 'scale' | null;
  onTransformChange?: (t: { position: [number, number, number]; rotation: [number, number, number]; scale: [number, number, number] }) => void;
}

export const AdminPreview = ({ draft, areaConfig, showLabels = false, showAxes = true, showOriginAxes = false, activeLabelId, onMeshClick, onLabelClick, onCameraControlsReady, placingLabelId, forceVisible, transformMode, onTransformChange }: AdminPreviewProps) => {
  const cameraControlsRef = useRef<CameraControls | null>(null);
  const hasModel = !!draft.config3D._modelBlobUrl;

  const cameraControlsCallback = useCallback(
    (controls: CameraControls | null) => {
      cameraControlsRef.current = controls;
      if (controls) {
        controls.mouseButtons.right = 8;
        controls.touches.two = 16384;
        controls.touches.three = 512;
      }
      onCameraControlsReady?.(controls);
    },
    [onCameraControlsReady]
  );

  const resetCamera = () => cameraControlsRef.current?.reset(true);

  return (
    <div
      className="w-full h-full rounded border border-[#333] overflow-hidden relative"
      style={{ background: 'radial-gradient(transparent 20%, rgb(8, 8, 8)), rgb(88, 88, 88)' }}
      onDoubleClick={resetCamera}
    >
      {hasModel && (
        <button
          onClick={(e) => { e.stopPropagation(); resetCamera(); }}
          onDoubleClick={(e) => e.stopPropagation()}
          className="absolute top-2 right-2 z-10 px-2 py-1 rounded text-xs bg-black/50 text-[#CCC] hover:bg-black/70 hover:text-white transition-colors select-none"
          title="Reset view (or double-click canvas)"
        >
          ↺ Reset
        </button>
      )}
      {transformMode && (
        <div className="absolute top-2 left-2 z-10 px-2 py-1 rounded text-xs bg-black/70 text-[#C3D82E] pointer-events-none select-none">
          {transformMode === 'translate' ? '↔ Move' : transformMode === 'rotate' ? '↻ Rotate' : '⇲ Scale'} · orbit paused
        </div>
      )}
      {!hasModel ? (
        <div className="flex items-center justify-center h-full text-[#666] text-sm">
          Upload an FBX model to preview
        </div>
      ) : (
        <Canvas
          gl={{ precision: 'highp', toneMapping: NoToneMapping, autoClear: false }}
          dpr={window.devicePixelRatio}
          camera={{ position: [0, 0, draft.config3D.dist], fov: 45, near: 0.1, far: 10000 }}
        >
          <CameraControls
            makeDefault
            ref={cameraControlsCallback}
            draggingSmoothTime={0.3}
            distance={draft.config3D.dist}
            minDistance={draft.config3D.distMin}
            maxDistance={draft.config3D.distMax}
          />
          <NavigationControlsEventListener resetViewHandler={resetCamera} cameraControlsRef={cameraControlsRef} />
          {showOriginAxes && <axesHelper args={[draft.config3D.distMax]} />}
          <Suspense fallback={<Loader />}>
            <AdminSubject
              draft={draft}
              areaConfig={areaConfig}
              onMeshClick={onMeshClick}
              onLabelClick={onLabelClick}
              labels={showLabels ? draft.labels : []}
              placingLabelId={placingLabelId}
              activeLabelId={activeLabelId}
              showAxes={showAxes}
              forceVisible={forceVisible}
              transformMode={transformMode}
              onTransformChange={onTransformChange}
            />
          </Suspense>
        </Canvas>
      )}
    </div>
  );
};
